// §branded-product-links — one entry point per branded product, so a Wegovy-pen
// campaign and a Foundayo campaign are distinguishable afterwards and each one
// reaches checkout with the medication its ad promised.
//
//   /branded/{slug}?t={intakeKey}
//
// Before this, /branded-weight-loss was a single door for every branded product:
// seven campaigns, one URL, and a patient who clicked a Zepbound ad landed on
// whichever SKU sorted cheapest.
//
// This registry is the SINGLE source of truth for the slug strings. The dashboard
// builds the same URLs from its own list, so these have to match byte-for-byte —
// confirmed with the dashboard team 2026-09-04. Changing a slug here without
// telling them silently breaks whatever ads already carry the old one.
//
// See docs/FE_SCOPE_BRANDED_PRODUCT_LINKS.md §1.

// `skuMatch` joins a slug to catalog rows BY PRODUCT NAME, because the catalog
// carries no manufacturer or dosage-form field (GET /catalog/medications returns
// name/price/dispenseType/fulfillment and nothing that says "pen" or "pill").
//
// That makes an upstream rename the failure mode to fear: a renamed SKU silently
// drops out of its slug, and the patient reaches a checkout offering the wrong
// product or none at all. The partition test in brandedProducts.test.js is what
// turns that into a failing build instead of a wrong prescription — it asserts
// every branded catalog row matches EXACTLY ONE slug. Do not weaken it.
//
// Verified against the live catalog 2026-09-04: 31 branded rows, 31 matched,
// none ambiguous, none unmatched.
export const BRANDED_PRODUCTS = {
  'wegovy-pen': {
    slug: 'wegovy-pen',
    label: 'Wegovy® (pen)',
    // Anchored + digit-guarded so "Wegovy Tablets …" cannot fall in here.
    skuMatch: /^wegovy\s+\d/i,
    manufacturer: 'novo',
    fastedWindow: false,
    live: true,
  },
  'ozempic-pen': {
    slug: 'ozempic-pen',
    label: 'Ozempic® (pen)',
    // `.25mg` is a real catalog spelling, hence the optional leading dot.
    skuMatch: /^ozempic\s+\.?\d/i,
    manufacturer: 'novo',
    fastedWindow: false,
    live: true,
  },
  'zepbound-pen': {
    slug: 'zepbound-pen',
    label: 'Zepbound® (pen)',
    skuMatch: /^zepbound/i,
    manufacturer: 'lilly',
    fastedWindow: false,
    live: true,
  },
  'mounjaro-pen': {
    slug: 'mounjaro-pen',
    label: 'Mounjaro® (pen)',
    skuMatch: /^mounjaro/i,
    manufacturer: 'lilly',
    fastedWindow: false,
    live: true,
  },
  'wegovy-pill': {
    slug: 'wegovy-pill',
    label: 'Wegovy® (pill)',
    skuMatch: /wegovy\s+tablets/i,
    manufacturer: 'novo',
    // Oral semaglutide: empty stomach on waking, <=4 oz plain water, wait before
    // food/drink/any other oral medication. Gate not built yet — §6A, Phase 2.
    fastedWindow: true,
    live: true,
  },
  'foundayo-pill': {
    slug: 'foundayo-pill',
    label: 'Foundayo® (pill)',
    skuMatch: /foundayo/i,
    manufacturer: 'lilly',
    // Orforglipron carries no fasted restriction — which is why this is where
    // §6A routes a patient who declines the acknowledgement.
    fastedWindow: false,
    live: true,
  },
  'ozempic-pill': {
    slug: 'ozempic-pill',
    label: 'Ozempic® (pill)',
    skuMatch: /ozempic\s+tablets/i,
    manufacturer: 'novo',
    // Set NOW, before the SKU exists. Oral semaglutide carries the same fasted
    // restriction as wegovy-pill, and every other line of the plan calls seeding
    // this product "a one-line addition" — which is exactly what makes an
    // unseeded product the dangerous case rather than the exempt one. A safety
    // requirement must not depend on whoever adds the SKU remembering it.
    fastedWindow: true,
    // Ozempic tablets are FDA-approved (1.5/4/9 mg, US launch 2026-05-04) but
    // Beluga has no medId for them yet, so the catalog returns zero rows. The
    // route resolves; the link is not distributable. Flip to true only once the
    // SKUs exist — a live link with no SKUs walks the patient through the entire
    // intake and dead-ends at checkout, after they have answered everything.
    live: false,
  },
};

// Canonical order — how the products are listed anywhere they're enumerated.
export const BRANDED_PRODUCT_SLUGS = Object.keys(BRANDED_PRODUCTS);

// Slugs whose links may actually be handed to a clinic. `ozempic-pill` is
// deliberately absent until Beluga seeds it.
export const DISTRIBUTABLE_BRANDED_SLUGS = BRANDED_PRODUCT_SLUGS.filter(
  (s) => BRANDED_PRODUCTS[s].live,
);

const norm = (s) => String(s || '').trim().toLowerCase();

/**
 * Look up a product by URL slug.
 * @param {string} slug
 * @returns {object|null} the registry entry, or null for unknown/not-yet-live.
 *   Both cases are null on purpose: the route handles them identically by
 *   redirecting to the all-products intake, so a retired or premature link
 *   degrades into a working form rather than a blank page.
 */
export function resolveBrandedProduct(slug) {
  const p = BRANDED_PRODUCTS[norm(slug)];
  if (!p || !p.live) return null;
  return p;
}

// Catalog predicate for a product. Passed to CatalogCheckoutStep as `filter`,
// which narrows both the recommendation and the "Modify your plan" list.
//
// Deliberately narrowing, not locking: the PRD is explicit that the medication is
// PRE-SELECTED but not fixed, because the flow's own injection-ability question
// can contradict the link the patient arrived on. They must be able to move.
export const brandedProductFilter = (product) => (item) =>
  Boolean(product) && product.skuMatch.test((item && (item.name || item.medication)) || '');

// Every branded SKU, for the all-products door (/branded-weight-loss). Derived
// from the registry rather than repeating the brand list, so a new product is
// added in exactly one place.
export const anyBrandedProductFilter = (item) => {
  const name = (item && (item.name || item.medication)) || '';
  return BRANDED_PRODUCT_SLUGS.some((s) => BRANDED_PRODUCTS[s].skuMatch.test(name));
};

// The routing DECISION, kept here as a pure function rather than inline in the
// route element, so it can be tested without standing up a router.
//
// It matters that this is exhaustively tested: the failure it guards is a slug
// that renders nothing. Every path on this host returns HTTP 200 (Vercel's CRA
// preset serves the SPA shell at any depth), so a slug with no handling is not a
// 404 — it is a blank white page, which a clinic cannot diagnose and which looks
// identical to "the page is still loading".
//
// @param {string|undefined} slug  the :slug route param; undefined on the
//   all-products door (/branded-weight-loss)
// @returns {{action:'render'|'redirect', product:object|null, redirectTo?:string}}
export const ALL_PRODUCTS_PATH = '/branded-weight-loss';

export function resolveBrandedRoute(slug) {
  if (!slug) return { action: 'render', product: null };   // all-products door
  const product = resolveBrandedProduct(slug);
  if (product) return { action: 'render', product };
  // Unknown, malformed, or registered-but-not-live (ozempic-pill). All three
  // redirect: a retired or premature link degrades into a working intake rather
  // than a blank page, and ozempic-pill in particular must never open a form
  // that walks the patient through every clinical question and then dead-ends at
  // a checkout with no SKUs.
  return { action: 'redirect', product: null, redirectTo: ALL_PRODUCTS_PATH };
}

// Strength in mg, parsed from the catalog name. The catalog has no dose field —
// same reason skuMatch joins on the name.
const doseMg = (item) => {
  const m = /([\d.]+)\s*mg/i.exec((item && (item.name || item.medication)) || '');
  const n = m ? parseFloat(m[1]) : NaN;
  return Number.isFinite(n) ? n : Infinity; // unparseable sorts last, never first
};

/**
 * Which SKU a product link opens on. Passed to CatalogCheckoutStep as
 * `recommendPick`; without it the checkout falls back to the CHEAPEST SKU.
 *
 * Cheapest is wrong for a titration drug, and the walk on 2026-09-05 showed how
 * wrong: five Mounjaro strengths (2.5 / 5 / 7.5 / 10 / 12.5 mg) all cost $970.40,
 * so "cheapest" tie-broke on catalog order and landed on **10mg** — a
 * mid-titration dose — as the default for every patient arriving from a Mounjaro
 * ad. The other five products only looked correct by luck: their price happens to
 * rise with dose, so cheapest and lowest coincided.
 *
 * Lowest dose is the defensible default for a starting preference. It is still a
 * default, and Phase 4 removes defaults entirely (a dose should not be chosen by
 * a sort at all) — until then this at least sorts on the clinically relevant axis
 * rather than on price.
 *
 * Ties break on price, then on name, so the pick is deterministic rather than
 * dependent on the order the catalog happened to return.
 */
export const lowestDosePick = (items) => {
  if (!items || !items.length) return null;
  return items.slice().sort((a, b) =>
    doseMg(a) - doseMg(b)
    || (a.price || 0) - (b.price || 0)
    || String(a.name || '').localeCompare(String(b.name || ''))
  )[0];
};
